/**
 * Auth Store (Pinia)
 *
 * @version 2.0.0
 * @description JWT-based authentication state management — aligned with Vuex auth module
 * @migrated-from store/auth/auth.ts (Vuex 4)
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { standardizeErrorMessage } from '@/utils/utils'

// =============================================================================
// Types
// =============================================================================

import type { User } from '@/models'
export type { User }

export interface LoginCredentials {
  email: string
  password: string
}

export interface LoginResponse {
  user: User
  access_token: string
}

/**
 * Callback hooks for cross-store side-effects (websocket, navigation, etc).
 * Set from main.ts or a boot composable during Fase 7 component migration.
 * Avoids circular imports between stores.
 */
export interface AuthStoreHooks {
  onLogin?: () => void | Promise<void>
  /**
   * Fired BEFORE the /logout API call invalidates the JWT.
   * Use this for any cleanup that needs to make authenticated requests
   * (e.g. unregistering push subscriptions server-side).
   */
  beforeLogout?: () => void | Promise<void>
  onLogout?: () => void | Promise<void>
  onUserLoaded?: () => void | Promise<void>
}

// =============================================================================
// Store
// =============================================================================

export const useAuthStore = defineStore('auth', () => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const authenticated = ref(false)
  const loadedSession = ref(false)

  // ---------------------------------------------------------------------------
  // Hooks (set externally to avoid circular deps)
  // ---------------------------------------------------------------------------

  let _hooks: AuthStoreHooks = {}

  function setHooks(hooks: AuthStoreHooks): void {
    _hooks = hooks
  }

  // ---------------------------------------------------------------------------
  // Getters — Role checks (aligned 1:1 with Vuex)
  // ---------------------------------------------------------------------------

  const isAuthenticated = computed(() => authenticated.value && !!user.value)

  const userRole = computed<string | null>(() => {
    if (user.value?.role) return user.value.role
    if (user.value?.roles?.length) return user.value.roles[0].name
    return null
  })

  const agentId = computed(() => user.value?.agent_id ?? null)

  const isAdmin = computed<boolean>(() => {
    return !!user.value?.role?.includes('admin') || !!user.value?.roles?.some((r: any) => r.name === 'admin')
  })

  // R1 (19/08, de-roling): RIMOSSI i getter isTechnicianBoss /
  // isTechnicianButNotBoss — la distinzione boss/non-boss in UI passa dai
  // permessi (technicians.manage, rapportini.edit.squad|all,
  // rapportini.call.open.any, reports.tech.*). Non reintrodurli.

  //  R1 (19/08, de-roling): RIMOSSI anche tutti quelli Commercial-like

  const isCst = computed<boolean>(() => {
    return !!user.value?.role?.includes('cst') || !!user.value?.roles?.some((r: any) => r.name === 'cst')
  })

  const isTotem = computed<boolean>(() => {
    return !!user.value?.role?.includes('totem')
  })

  /**
   * super-admin (ruolo tecnico con bypass Gate::before lato BE). Serve SOLO per
   * strumenti di collaudo (es. data simulata commesse): le feature di business
   * restano su can()/canAny(), non su questo getter.
   */
  const isSuperAdmin = computed<boolean>(() => {
    return user.value?.role === 'super-admin' || !!user.value?.roles?.some((r: any) => r.name === 'super-admin')
  })

  /**
   * R1 — sconto libero nei preventivi: ora è il permesso `quotes.discount.free`
   * (BE v1.105.0: admin via ruolo + grant diretto all'utente ex-hardcodato).
   * Il check su email hardcodata è morto qui. Nome del getter invariato:
   * tutti i call-site (useQuoteUtils, viste preventivi) continuano a funzionare.
   */
  const freeQuoteDiscount = computed(() => {
    return (user.value?.permissions ?? []).includes('quotes.discount.free')
  })

  // ---------------------------------------------------------------------------
  // Getters — Permissions (R0)
  // ---------------------------------------------------------------------------
  // Permessi effettivi flattened, forniti dal BE in login/me (`permissions`).
  // super-admin riceve dal BE l'elenco completo, quindi can() funziona sempre.
  // I getter di ruolo qui sopra sono DEPRECATI: le nuove feature usano can(),
  // i getter esistenti migreranno modulo per modulo in R1.

  const permissions = computed<string[]>(() => user.value?.permissions ?? [])

  /** True se l'utente ha il permesso indicato (es. 'rapportini.view.all'). */
  function can(permission: string): boolean {
    return permissions.value.includes(permission)
  }

  /** True se l'utente ha ALMENO UNO dei permessi indicati. */
  function canAny(...perms: string[]): boolean {
    return perms.some((p) => permissions.value.includes(p))
  }

  /** True se l'utente ha TUTTI i permessi indicati. */
  function canAll(...perms: string[]): boolean {
    return perms.every((p) => permissions.value.includes(p))
  }

  // ---------------------------------------------------------------------------
  // Impersonation (R1)
  // ---------------------------------------------------------------------------
  // JWT stateless: impersonare = usare il token del target. La sessione
  // originale (token+user) resta in localStorage e si ripristina all'uscita.
  // BE: POST /user/{id}/impersonate (permesso users.impersonate; audit log).

  const IMPERSONATOR_TOKEN_KEY = 'impersonator_token'
  const IMPERSONATOR_USER_KEY = 'impersonator_user'

  const isImpersonating = computed(() => !!localStorage.getItem(IMPERSONATOR_TOKEN_KEY))

  const impersonatorName = computed<string>(() => {
    try {
      const stored = localStorage.getItem(IMPERSONATOR_USER_KEY)
      if (!stored) return ''
      const u = JSON.parse(stored)
      return [u.name, u.surname].filter(Boolean).join(' ')
    } catch {
      return ''
    }
  })

  /**
   * Impersona l'utente indicato: salva la sessione corrente, adotta la nuova
   * e ricarica l'app (stato pulito su tutti gli store).
   */
  async function impersonate(userId: number): Promise<void> {
    const response = await axios.post<LoginResponse>(`/user/${userId}/impersonate`)
    const { user: targetUser, access_token } = response.data

    // Backup della sessione originale (solo se non già in impersonation:
    // impersonation annidata = si conserva sempre l'identità di partenza)
    if (!isImpersonating.value) {
      localStorage.setItem(IMPERSONATOR_TOKEN_KEY, token.value ?? '')
      localStorage.setItem(IMPERSONATOR_USER_KEY, JSON.stringify(user.value ?? {}))
    }

    _setToken(access_token)
    _setUser(targetUser as User)
    window.location.assign('/')
  }

  /** Ripristina la sessione originale e ricarica l'app. */
  function stopImpersonation(): void {
    const storedToken = localStorage.getItem(IMPERSONATOR_TOKEN_KEY)
    const storedUser = localStorage.getItem(IMPERSONATOR_USER_KEY)
    if (!storedToken) return

    _setToken(storedToken)
    try {
      _setUser(storedUser ? JSON.parse(storedUser) : null)
    } catch {
      _setUser(null)
    }
    localStorage.removeItem(IMPERSONATOR_TOKEN_KEY)
    localStorage.removeItem(IMPERSONATOR_USER_KEY)
    window.location.assign('/')
  }

  // ---------------------------------------------------------------------------
  // Actions — Auth lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Login with email/password credentials.
   * Stores JWT token and user data, then fires onLogin hook (websocket reconnect etc).
   */
  async function login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await axios.post<LoginResponse>('/login', credentials)
      const { user: userData, access_token } = response.data

      _setToken(access_token)
      _setUser(userData)
      authenticated.value = true
      _persistAuthenticated(true)

      // Fire hook (websocket reconnect, channel subscriptions)
      await _hooks.onLogin?.()

      return response.data
    } catch (error) {
      throw standardizeErrorMessage(error)
    }
  }

  /**
   * Logout and invalidate token.
   * Fires beforeLogout BEFORE the API call (so authenticated cleanup can run
   * with a still-valid JWT), then onLogout AFTER, even if API call fails.
   */
  async function logout(): Promise<void> {
    // Pre-logout cleanup with still-valid JWT (e.g. push unsubscribe).
    // Errors here are non-fatal — we always proceed with the actual logout.
    try {
      await _hooks.beforeLogout?.()
    } catch (error) {
      console.warn('beforeLogout hook error:', error)
    }

    try {
      if (token.value) {
        await axios.post('/logout')
      }
    } catch (error) {
      console.warn('Logout API error:', error)
    } finally {
      await resetUser()
    }
  }

  /**
   * Clear all auth state. Called on logout and on 401 hard-fail.
   */
  async function resetUser(): Promise<void> {
    await _hooks.onLogout?.()
    authenticated.value = false
    _setUser(null)
    _setToken(null)
    _persistAuthenticated(false)
  }

  /**
   * Fetch current user data from API.
   * Verifies token validity and refreshes user data.
   */
  async function me(): Promise<User> {
    try {
      const response = await axios.get<User>('/me')
      authenticated.value = true
      _persistAuthenticated(true)

      // Only update user if data actually changed
      if (JSON.stringify(response.data) !== JSON.stringify(user.value)) {
        _setUser(response.data)
      }

      // Hook (sottoscrizioni websocket, reattach delle push): e' un EFFETTO
      // COLLATERALE, non parte dell'autenticazione. Stava dentro il try
      // esterno, quindi un suo guasto finiva nel catch qui sotto e azzerava
      // utente e token — una sessione appena verificata dal server veniva
      // buttata via perche' Reverb non rispondeva.
      try {
        await _hooks.onUserLoaded?.()
      } catch (hookError) {
        console.error('onUserLoaded fallito (sessione mantenuta):', hookError)
      }

      return response.data
    } catch (error) {
      authenticated.value = false
      _setUser(null)
      _setToken(null)
      _persistAuthenticated(false)
      throw standardizeErrorMessage(error)
    }
  }

  /**
   * Refresh JWT token.
   */
  async function refreshToken(): Promise<string> {
    try {
      const response = await axios.post<{ access_token: string; user?: User }>('/refresh')
      const { access_token, user: userData } = response.data
      _setToken(access_token)
      if (userData) _setUser(userData)
      return access_token
    } catch (error) {
      throw standardizeErrorMessage(error)
    }
  }

  // ---------------------------------------------------------------------------
  // Actions — Password reset
  // ---------------------------------------------------------------------------

  async function forgot(credentials: { email: string }): Promise<any> {
    try {
      const response = await axios.post('/forgot', credentials)
      return response
    } catch (error) {
      throw standardizeErrorMessage(error)
    }
  }

  async function verify(credentials: { code: string; email?: string }): Promise<any> {
    try {
      const response = await axios.post('/verify', credentials)
      return response
    } catch (error) {
      throw standardizeErrorMessage(error)
    }
  }

  async function reset(credentials: Record<string, any>): Promise<any> {
    try {
      const response = await axios.post('/reset', credentials)
      return response
    } catch (error) {
      throw standardizeErrorMessage(error)
    }
  }

  // ---------------------------------------------------------------------------
  // Actions — Session management
  // ---------------------------------------------------------------------------

  /**
   * Load auth state from localStorage on app init.
   */
  function loadSession(): void {
    const storedToken = localStorage.getItem('token')
    if (storedToken) token.value = storedToken

    const storedAuthenticated = localStorage.getItem('authenticated')
    if (storedAuthenticated === 'true') authenticated.value = true

    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser)
      } catch (e) {
        console.error('Failed to parse stored user:', e)
        localStorage.removeItem('user')
      }
    }

    loadedSession.value = true
    _listenToOtherTabs()
  }

  /**
   * Sincronizzazione fra schede (08/09/2026, «mi caccia fuori ogni poco»).
   *
   * Ogni scheda tiene il token in memoria; un refresh fatto da UN'ALTRA
   * scheda (il totem lo rinnova in anticipo, ma anche il middleware BE lo
   * rinnova alla scadenza) mette in blacklist il token vecchio, e la scheda
   * che lo usa ancora dopo il periodo di grazia (30 s) riceve 401, prova un
   * refresh con un token già in blacklist e viene sloggiata. Lo `storage`
   * event arriva a tutte le altre schede dello stesso origin: da qui si
   * allineano token e utente senza ri-persistere (è già in localStorage).
   * Un logout fatto altrove (token rimosso) viene invece lasciato gestire
   * al normale ciclo 401 → login della scheda.
   */
  let _tabsListenerInstalled = false
  function _listenToOtherTabs(): void {
    if (_tabsListenerInstalled || typeof window === 'undefined') return
    _tabsListenerInstalled = true
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.storageArea !== localStorage) return
      if (e.key === 'token' && e.newValue && e.newValue !== token.value) {
        token.value = e.newValue
      } else if (e.key === 'user' && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue) as User
          if (JSON.stringify(parsed) !== JSON.stringify(user.value)) user.value = parsed
        } catch { /* ignora un valore malformato scritto altrove */ }
      }
    })
  }

  // ---------------------------------------------------------------------------
  // Internal helpers — localStorage persistence
  // ---------------------------------------------------------------------------

  function _setUser(userData: User | null): void {
    user.value = userData
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData))
    } else {
      localStorage.removeItem('user')
    }
  }

  function _setToken(newToken: string | null): void {
    token.value = newToken
    if (newToken) {
      localStorage.setItem('token', newToken)
    } else {
      localStorage.removeItem('token')
    }
  }

  function _persistAuthenticated(value: boolean): void {
    localStorage.setItem('authenticated', String(value))
  }

  // ---------------------------------------------------------------------------
  // Expose for external use (Vuex compat: commit('setUser', ...) → setUser())
  // ---------------------------------------------------------------------------

  function setUser(userData: User | null): void {
    _setUser(userData)
  }

  function setToken(newToken: string | null): void {
    _setToken(newToken)
  }

  function setAuthenticated(value: boolean): void {
    authenticated.value = value
    _persistAuthenticated(value)
  }

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    // State
    user,
    token,
    authenticated,
    loadedSession,

    // Getters
    isAuthenticated,
    userRole,
    agentId,
    isAdmin,
    isCst,
    isTotem,
    isSuperAdmin,
    freeQuoteDiscount,

    // Getters — Permissions (R0)
    permissions,
    can,
    canAny,
    canAll,

    // Impersonation (R1)
    isImpersonating,
    impersonatorName,
    impersonate,
    stopImpersonation,

    // Actions — Auth lifecycle
    login,
    logout,
    resetUser,
    me,
    refreshToken,

    // Actions — Password reset
    forgot,
    verify,
    reset,

    // Actions — Session
    loadSession,

    // Actions — Setters (Vuex mutation compat)
    setUser,
    setToken,
    setAuthenticated,

    // Actions — Hooks
    setHooks,
  }
})
